import React, {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {observer} from 'mobx-react-lite';
import {faqStore} from 'app/store/global';
import {scrollToTop} from 'app/utils';
import SmartFullPageLoader from 'app/components/reusable/smart_loaders/SmartFullPageLoader';
import type {FaqCategoryKey} from 'app/utils/knowledgeCenter/categories';
import {faqSlugsMatch} from 'app/utils/knowledgeCenter/faqSlug';
import KnowledgeCenterArticleLayout from './KnowledgeCenterArticleLayout';
import KnowledgeCenterEmptyState from './KnowledgeCenterEmptyState';
import KnowledgeCenterSectionAside from './KnowledgeCenterSectionAside';
import KnowledgeCenterHelpfulVote from './KnowledgeCenterHelpfulVote';

const KnowledgeCenterArticlePage = observer((): React.ReactElement => {
    const {id} = useParams<{id: string}>();
    const slug = id ?? '';

    useEffect(() => {
        faqStore.loadMeta().catch(() => null);
    }, []);

    useEffect(() => {
        const hasFullArticle = faqStore.faqs.some(
            faq => faqSlugsMatch(faq.name, slug) && Boolean(faq.text)
        );

        if (hasFullArticle) {
            return;
        }

        faqStore.loadData().catch(() => null);
    }, [slug]);

    useEffect(() => {
        scrollToTop();
    }, [slug]);

    const article = faqStore.faqs.find(faq => faqSlugsMatch(faq.name, slug));
    const isLoading = faqStore.loading && !article?.text;

    if (isLoading) {
        return <SmartFullPageLoader />;
    }

    if (!article) {
        return <KnowledgeCenterEmptyState />;
    }

    const categoryKey: FaqCategoryKey | null = article.category && faqStore.isKnownCategory(article.category)
        ? article.category
        : null;

    const sectionArticles = categoryKey
        ? faqStore.faqsByCategory[categoryKey]
        : [];

    return (
        <KnowledgeCenterArticleLayout
            category={categoryKey}
            categories={faqStore.faqCategories}
            articleTitle={article.title}
        >
            <div className="knowledge-center__article-column">
                <div className="knowledge-center__article-main">
                    <article className="knowledge-center__article">
                        <h1 className="knowledge-center__article-title">{article.title}</h1>
                        <div
                            className="knowledge-center__article-content description-question"
                            dangerouslySetInnerHTML={{__html: article.text}}
                        />
                    </article>
                    <KnowledgeCenterHelpfulVote
                        key={article.name}
                        articleSlug={article.name}
                    />
                </div>
                {categoryKey && <KnowledgeCenterSectionAside
                    currentSlug={article.name}
                    articles={sectionArticles}
                    categoryKey={categoryKey}
                />}
            </div>
        </KnowledgeCenterArticleLayout>
    );
});

export default KnowledgeCenterArticlePage;
